import { createSlice, createSelector } from '@reduxjs/toolkit'
import { fetchTemplatesForEdit, addContainersToMacroProcess } from './services/macroProcessEditService'

const initialState = {
  modalOpen: false,
  activeMacroProcess: null, // { id, purpose, process_containers[] }
  availableTemplates: [],
  selectedTemplates: [], // [{ templateId, position }]
  loadingStatus: {
    fetchTemplates: 'idle',
    addContainers: 'idle',
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {},
  },
}

const macroProcessEditSlice = createSlice({
  name: 'macroProcessEdit',
  initialState,
  reducers: {
    openEditModal: (state, action) => {
      state.modalOpen = true
      state.activeMacroProcess = action.payload
      state.availableTemplates = []
      state.selectedTemplates = []
      state.requestStatus = initialState.requestStatus
    },
    closeEditModal: (state) => {
      state.modalOpen = false
      state.activeMacroProcess = null
      state.availableTemplates = []
      state.selectedTemplates = []
      state.requestStatus = initialState.requestStatus
    },
    toggleTemplate: (state, action) => {
      const templateId = action.payload
      const index = state.selectedTemplates.findIndex(t => t.templateId === templateId)
      if (index !== -1) {
        state.selectedTemplates.splice(index, 1)
      } else {
        const existingPositions = (state.activeMacroProcess?.process_containers || [])
          .filter(c => c.status === 'pending')
          .map(c => c.position)
        const selectedPositions = state.selectedTemplates.map(t => t.position)
        const takenPositions = new Set([...existingPositions, ...selectedPositions])
        let nextPosition = 1
        while (takenPositions.has(nextPosition)) {
          nextPosition++
        }
        state.selectedTemplates.push({ templateId, position: nextPosition })
      }
    },
    setTemplatePosition: (state, action) => {
      const { templateId, position } = action.payload
      const item = state.selectedTemplates.find(t => t.templateId === templateId)
      if (item) {
        item.position = position
      }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
  },
  extraReducers: (builder) => {
    // fetchTemplatesForEdit
    builder.addCase(fetchTemplatesForEdit.pending, (state) => {
      state.loadingStatus.fetchTemplates = 'pending'
    })
    builder.addCase(fetchTemplatesForEdit.fulfilled, (state, action) => {
      state.loadingStatus.fetchTemplates = 'fulfilled'
      const availableTemplates = action.payload.templates || []
      const activeMacroProcess = state.activeMacroProcess
      if(availableTemplates.length > 0 && activeMacroProcess ) {
        availableTemplates.sort( (t1, t2) => {
          const c1 = activeMacroProcess.process_containers.find(c => c.process_template_id === t1.id)
          const c2 = activeMacroProcess.process_containers.find(c => c.process_template_id === t2.id)
          return (c1?.position ?? 0) - (c2?.position ?? 0)
        })
      }
      state.availableTemplates = availableTemplates
    })
    builder.addCase(fetchTemplatesForEdit.rejected, (state) => {
      state.loadingStatus.fetchTemplates = 'rejected'
    })

    // addContainersToMacroProcess
    builder.addCase(addContainersToMacroProcess.pending, (state) => {
      state.loadingStatus.addContainers = 'pending'
      state.requestStatus = initialState.requestStatus
    })
    builder.addCase(addContainersToMacroProcess.fulfilled, (state, action) => {
      state.loadingStatus.addContainers = 'fulfilled'
      // Mettre à jour le activeMacroProcess avec les données fraîches
      state.activeMacroProcess = action.payload
      state.modalOpen = false
      state.selectedTemplates = []
      state.requestStatus.success = true
    })
    builder.addCase(addContainersToMacroProcess.rejected, (state, action) => {
      state.loadingStatus.addContainers = 'rejected'
      state.error = action.payload
      const errors = action.payload?.errors ? action.payload.errors[0] : {}
      if(errors?.position) {
        errors.position = "Les sous-processes doivent avoir un ordre unique"
      }
      state.requestStatus.error = action.payload || 'Erreur lors de l\'ajout'
      state.requestStatus.errors = errors 
      state.requestStatus.success = false
    })
  },
})

export const {
  openEditModal,
  closeEditModal,
  toggleTemplate,
  setTemplatePosition,
  resetRequestStatus,
} = macroProcessEditSlice.actions


const EMPTY_CONTAINERS = []

// Map { templateId -> container } mémoïsée — un seul useSelector dans le composant
export const selectContainersByTemplateId = createSelector(
  state => state.macroProcessEdit.activeMacroProcess?.process_containers ?? EMPTY_CONTAINERS,
  containers => containers
    .filter(c => c.status === 'pending')
    .reduce((acc, c) => {
      acc[c.process_template_id] = c
      return acc
    }, {})
)

export default macroProcessEditSlice.reducer
